import { useState, useEffect, useCallback } from 'react';

/**
 * Hook para manejar navegación por teclado/control remoto en modo TV.
 * Permite navegar con las flechas del teclado y seleccionar con Enter.
 *
 * @param {boolean} enabled - Si la navegación TV está habilitada
 * @returns {object} { focusedIndex, setFocusedIndex, handleKeyDown }
 */
export const useTVNavigation = (enabled = false) => {
  const [focusedIndex, setFocusedIndex] = useState(0);

  /**
   * Obtiene todos los elementos focusable en el DOM
   */
  const getFocusableElements = useCallback(() => {
    const selectors = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '.tv-focusable' // Clase personalizada para elementos focusables en TV
    ];

    return document.querySelectorAll(selectors.join(', '));
  }, []);

  /**
   * Mueve el foco al siguiente elemento
   */
  const moveFocus = useCallback((direction) => {
    const elements = getFocusableElements();
    if (elements.length === 0) return;

    const currentIndex = Array.from(elements).findIndex(
      el => el === document.activeElement
    );

    let nextIndex;

    switch (direction) {
      case 'next':
        nextIndex = currentIndex + 1;
        if (nextIndex >= elements.length) nextIndex = 0;
        break;
      case 'prev':
        nextIndex = currentIndex - 1;
        if (nextIndex < 0) nextIndex = elements.length - 1;
        break;
      case 'first':
        nextIndex = 0;
        break;
      default:
        nextIndex = currentIndex;
    }

    const nextElement = elements[nextIndex];
    if (nextElement) {
      nextElement.focus();
      setFocusedIndex(nextIndex);
    }
  }, [getFocusableElements]);

  /**
   * Maneja eventos de teclado para navegación TV
   */
  const handleKeyDown = useCallback((e) => {
    if (!enabled) return;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        moveFocus('next');
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        moveFocus('prev');
        break;
      case 'Enter':
      case ' ':
        // Permitir que el evento continúe para activar botones
        if (document.activeElement && document.activeElement.tagName === 'BUTTON') {
          // Dejar que el evento nativo maneje el click
          return;
        }
        e.preventDefault();
        document.activeElement?.click();
        break;
      case 'Escape':
        // Permitir cerrar modales o volver atrás
        return;
      default:
        return;
    }
  }, [enabled, moveFocus]);

  /**
   * Registra el event listener de teclado
   */
  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);

  /**
   * Enfoca el primer elemento cuando se habilita la navegación TV
   */
  useEffect(() => {
    if (enabled) {
      // Pequeño delay para asegurar que el DOM esté listo
      const timeoutId = setTimeout(() => {
        moveFocus('first');
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [enabled, moveFocus]);

  return {
    focusedIndex,
    setFocusedIndex,
    handleKeyDown,
    moveFocus
  };
};

/**
 * Hook simplificado para hacer un elemento focusable en TV
 */
export const useTVFocusable = (enabled = false, onClick = null) => {
  const attrs = enabled ? {
    tabIndex: 0,
    className: 'tv-focusable',
    role: 'button',
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (onClick) onClick();
      }
    }
  } : {};

  return attrs;
};

export default useTVNavigation;
